<template>
  <div class="card">
    <div class="card-header bg-info text-white">
      <h5 class="mb-0">报站模板配置</h5>
    </div>
    <div class="card-body p-0">
      <div class="row g-0">
        <!-- 左侧：站点选择模块 -->
        <div class="col-md-4 border-end">
          <div class="p-3">
            <h6 class="mb-3">站点选择</h6>

            <!-- 方向选择 -->
            <div class="btn-group d-flex justify-content-center mb-3" role="group" v-if="lineType === 'twoWay'">
              <button type="button" class="btn btn-sm btn-outline-primary" :class="{ 'active': currentDirection === 'up' }" @click="switchDirection('up')">上行</button>
              <button type="button" class="btn btn-sm btn-outline-primary" :class="{ 'active': currentDirection === 'down' }" @click="switchDirection('down')">下行</button>
            </div>

            <!-- 站点列表 -->
            <div class="station-list" id="stationList">
              <div v-if="!currentStations.length" class="text-center text-muted py-5">
                请导入或创建站名列表
              </div>
              <div v-else>
                <div v-for="(station, index) in currentStations" :key="station.id" 
                     class="station-item" 
                     :class="{ 'active': selectedStationIndex === index }"
                     @click="selectStation(index)">
                  <div>
                    <span class="badge bg-secondary me-2">{{ index + 1 }}</span>
                    {{ getStationDisplayName(station) }}
                    <span v-if="index === 0" class="badge bg-success badge-station-type">起点站</span>
                    <span v-else-if="index === currentStations.length - 1" class="badge bg-danger badge-station-type">终点站</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 站点排序控制 -->
            <div class="mt-3 d-flex justify-content-center" v-if="selectedStationIndex !== -1 && currentStations.length > 1">
              <button class="btn btn-sm btn-outline-secondary me-2" @click="moveStationUp" :disabled="selectedStationIndex === 0">
                <i class="bi bi-arrow-up"></i> 上移
              </button>
              <button class="btn btn-sm btn-outline-secondary" @click="moveStationDown" :disabled="selectedStationIndex === currentStations.length - 1">
                <i class="bi bi-arrow-down"></i> 下移
              </button>
            </div>
          </div>
        </div>
        
        <!-- 右侧：音频配置模块 -->
        <div class="col-md-8">
          <div class="p-3">
      <!-- 模板内容配置 -->
      <div class="template-container">
        <!-- 报站类型按钮导航：第一行（固定四类） -->
        <div class="station-type-btns mb-2">
          <button
            v-for="tab in mainTabs"
            :key="tab.value"
            type="button"
            class="btn btn-sm btn-outline-primary station-type-btn"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
            :title="tab.label"
          >
            {{ tab.label }}
          </button>
        </div>
        <!-- 第二行（服务语） -->
        <div class="station-type-btns mb-3">
          <button
            v-for="tab in serviceTabs"
            :key="tab.value"
            type="button"
            class="btn btn-sm btn-outline-primary station-type-btn"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
            :title="tab.label"
          >
            {{ tab.label }}
          </button>
        </div>
        
        <!-- 音频序列配置 -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6>{{ getActiveTabName() }}</h6>
            <!-- 上下行不同选项 -->
            <div v-if="['arrival', 'departure', 'welcome', 'terminal'].includes(activeTab)" class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                :id="'separate-' + activeTab"
                v-model="separateDirections[activeTab]"
                @change="onSeparateDirectionChange"
              >
              <label class="form-check-label" :for="'separate-' + activeTab">
                上下行不同
              </label>
            </div>
          </div>
          <div class="mb-3">
            <div class="row mb-2">
              <div class="col">
                <label class="form-label">可用音频列表</label>
                <select class="form-select" size="6" v-model="selectedAudio">
                  <template v-for="folder in audioFolders" :key="folder.name">
                    <optgroup :label="folder.name" v-if="folder.audios && folder.audios.length > 0">
                      <option v-for="audio in folder.audios" :key="audio.name" :value="audio">
                        {{ audio.name }}
                      </option>
                    </optgroup>
                  </template>
                  <optgroup label="暂无音频文件" v-if="!audioFolders.length || audioFolders.every(f => !f.audios || !f.audios.length)">
                    <option disabled>请先选择项目文件夹</option>
                  </optgroup>
                </select>
              </div>
              <div class="col-auto align-self-center">
                <div class="d-flex flex-column">
                  <button type="button" class="btn btn-sm btn-primary mb-2" @click="addToSequence">
                    添加
                  </button>
                  <button type="button" class="btn btn-sm btn-danger" @click="removeFromSequence" :disabled="selectedSequenceAudio === null">
                    移除
                  </button>
                </div>
              </div>
              <!-- 统一音频播放顺序 -->
              <div v-if="!(['arrival', 'departure', 'welcome', 'terminal'].includes(activeTab) && separateDirections[activeTab])" class="col">
                <label class="form-label">音频播放顺序</label>
                <select class="form-select" size="6" v-model="selectedSequenceAudio">
                  <option v-for="(audio, index) in currentSequence" :key="index" :value="index" :title="audioFullPath(audio)">
                    {{ index + 1 }}. {{ audioLabel(audio) }}
                  </option>
                </select>
                <div class="form-text">按顺序播放音频</div>
              </div>
              
              <!-- 上下行分别的音频播放顺序 -->
              <div v-else class="col">
                <div class="row">
                  <div class="col-6">
                    <label class="form-label">上行音频</label>
                    <select class="form-select" size="6" v-model="selectedSequenceAudio" @change="onSequenceSelectionChange('up')">
                      <option v-for="(audio, index) in getDirectionSequence('up')" :key="'up-' + index" :value="'up-' + index" :title="audioFullPath(audio)">
                        {{ index + 1 }}. {{ audioLabel(audio) }}
                      </option>
                    </select>
                  </div>
                  <div class="col-6">
                    <label class="form-label">下行音频</label>
                    <select class="form-select" size="6" v-model="selectedSequenceAudio" @change="onSequenceSelectionChange('down')">
                      <option v-for="(audio, index) in getDirectionSequence('down')" :key="'down-' + index" :value="'down-' + index" :title="audioFullPath(audio)">
                        {{ index + 1 }}. {{ audioLabel(audio) }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-text">按顺序播放音频（上行/下行分别设置）</div>
              </div>
            </div>
            
            <div class="row">
              <div class="col">
                <button type="button" class="btn btn-sm btn-outline-secondary me-1" @click="moveUp" :disabled="!canMoveUp">
                  <i class="bi bi-arrow-up"></i> 上移
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="moveDown" :disabled="!canMoveDown">
                  <i class="bi bi-arrow-down"></i> 下移
                </button>
              </div>
              <div class="col-auto align-self-center">
                <!-- 预览按钮：上下行不同时分别显示两个 -->
                <template v-if="['arrival', 'departure', 'welcome', 'terminal'].includes(activeTab) && separateDirections[activeTab]">
                  <button type="button" class="btn btn-sm btn-success me-2" @click="previewDirectionSequence('up')">
                    <i class="bi bi-play-fill"></i> 预览上行
                  </button>
                  <button type="button" class="btn btn-sm btn-success" @click="previewDirectionSequence('down')">
                    <i class="bi bi-play-fill"></i> 预览下行
                  </button>
                </template>
                <template v-else>
                  <button type="button" class="btn btn-sm btn-success" @click="previewAudioSequence">
                    <i class="bi bi-play-fill"></i> 预览
                  </button>
                </template>
              </div>
            </div>

            <!-- 音频占位符按钮区域 - 多语言启用时始终显示，与音频数据无关 -->
            <template v-if="['arrival', 'departure', 'welcome', 'terminal'].includes(activeTab) && separateDirections[activeTab]">
              <div class="d-flex flex-wrap gap-1 mb-2">
                <h6 class="w-100">音频占位符（上行）：</h6>
                <!-- 基于文件夹的站点占位符 -->
                <template v-for="folder in audioFolders" :key="'up-' + folder.name">
                  <div v-if="folder.audios && folder.audios.length > 0" class="w-100 mb-2">
                    <small class="text-muted">{{ folder.name }}文件夹：</small><br>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholderToDirection(activeTab, folder.name, 'current', 'up')">
                      {{ folder.name }}-当前站
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholderToDirection(activeTab, folder.name, 'terminal', 'up')">
                      {{ folder.name }}-终点站
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholderToDirection(activeTab, folder.name, 'first', 'up')">
                      {{ folder.name }}-起点站
                    </button>
                  </div>
                </template>
              </div>
              <div class="d-flex flex-wrap gap-1 mb-2">
                <h6 class="w-100">音频占位符（下行）：</h6>
                <!-- 基于文件夹的站点占位符 -->
                <template v-for="folder in audioFolders" :key="'down-' + folder.name">
                  <div v-if="folder.audios && folder.audios.length > 0" class="w-100 mb-2">
                    <small class="text-muted">{{ folder.name }}文件夹：</small><br>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholderToDirection(activeTab, folder.name, 'current', 'down')">
                      {{ folder.name }}-当前站
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholderToDirection(activeTab, folder.name, 'terminal', 'down')">
                      {{ folder.name }}-终点站
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholderToDirection(activeTab, folder.name, 'first', 'down')">
                      {{ folder.name }}-起点站
                    </button>
                  </div>
                </template>
              </div>
            </template>
            <template v-else>
              <div class="d-flex flex-wrap gap-1 mb-2">
                <h6 class="w-100">音频占位符：</h6>
                <!-- 基于文件夹的站点占位符 -->
                <template v-for="folder in audioFolders" :key="folder.name">
                  <div v-if="folder.audios && folder.audios.length > 0" class="w-100 mb-2">
                    <small class="text-muted">{{ folder.name }}文件夹：</small><br>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholder(activeTab, folder.name, 'current')">
                      {{ folder.name }}-当前站
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholder(activeTab, folder.name, 'terminal')">
                      {{ folder.name }}-终点站
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="addFolderPlaceholder(activeTab, folder.name, 'first')">
                      {{ folder.name }}-起点站
                    </button>
                  </div>
                </template>
                <!-- 如果没有文件夹，显示传统占位符 -->
                <div v-if="!audioFolders.length" class="w-100">
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="addPresetAudio(activeTab, 'current')">
                    插入【当前站】
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="addPresetAudio(activeTab, 'terminal')">
                    插入【终点站】
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="addPresetAudio(activeTab, 'first')">
                    插入【起点站】
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="addPresetAudio(activeTab, 'alias')">
                    插入【副站名】
                  </button>
                </div>
              </div>
            </template>
            
            <!-- 多语言占位符按钮 - 多语言启用时始终显示 -->
            <div v-if="languageSettings.enabled && languageSettings.count > 1" class="mt-2">
              <h6 class="w-100 mt-3 mb-2">多语言占位符</h6>
              <!-- 当前站多语言按钮 -->
              <div class="d-flex flex-wrap gap-1 mb-2">
                <template v-for="(lang, index) in visibleLanguages" :key="'current-'+index">
                  <button v-if="index > 0" type="button" class="btn btn-sm btn-outline-info" 
                    @click="addPresetAudio(activeTab, 'current'+(index+1))">
                    当前站({{lang.name}})
                  </button>
                </template>
              </div>
              <!-- 终点站多语言按钮 -->
              <div class="d-flex flex-wrap gap-1 mb-2">
                <template v-for="(lang, index) in visibleLanguages" :key="'terminal-'+index">
                  <button v-if="index > 0" type="button" class="btn btn-sm btn-outline-info" 
                    @click="addPresetAudio(activeTab, 'terminal'+(index+1))">
                    终点站({{lang.name}})
                  </button>
                </template>
              </div>
              <!-- 起点站多语言按钮 -->
              <div class="d-flex flex-wrap gap-1 mb-2">
                <template v-for="(lang, index) in visibleLanguages" :key="'first-'+index">
                  <button v-if="index > 0" type="button" class="btn btn-sm btn-outline-info" 
                    @click="addPresetAudio(activeTab, 'first'+(index+1))">
                    起点站({{lang.name}})
                  </button>
                </template>
              </div>
            </div>
          </div>
          
          <div class="mb-3">
            <h6>音频预览</h6>
            <div v-if="currentSequence.length > 0" class="audio-preview p-2">
              <div class="d-flex align-items-center">
                <button type="button" class="btn btn-sm btn-primary me-2" @click="previewAudioSequence">
                  <i class="bi bi-play-fill"></i> 预览播放
                </button>
                <div class="progress w-100" v-if="isPlaying">
                  <div class="progress-bar" role="progressbar" :style="{ width: playProgress + '%' }"></div>
                </div>
                <span v-else class="text-muted">点击预览按钮播放完整音频序列</span>
              </div>
            </div>
            <div v-else class="alert alert-warning">
              请先添加音频到播放顺序列表
            </div>
          </div>
        </div>
        

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TemplatePanel',
  emits: ['generateJson', 'updateLanguageSettings', 'selectStation', 'directionChanged', 'audioConfigChanged'],
  props: {
    jsonGenerated: {
      type: Boolean,
      default: false
    },
    audioFolders: {
      type: Array,
      default: () => []
    },
    upDirectionStations: {
      type: Array,
      default: () => []
    },
    downDirectionStations: {
      type: Array,
      default: () => []
    },
    currentDirection: {
      type: String,
      default: 'up'
    },
    selectedStationIndex: {
      type: Number,
      default: -1
    },
    languageSettings: {
      type: Object,
      required: true
    },
    lineType: {
      type: String,
      default: 'twoWay'
    },
    defaultArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    defaultDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    welcomeAudioSequence: {
      type: Array,
      default: () => []
    },
    terminalAudioSequence: {
      type: Array,
      default: () => []
    },
    upArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    downArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    upDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    downDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    upWelcomeAudioSequence: {
      type: Array,
      default: () => []
    },
    downWelcomeAudioSequence: {
      type: Array,
      default: () => []
    },
    upTerminalAudioSequence: {
      type: Array,
      default: () => []
    },
    downTerminalAudioSequence: {
      type: Array,
      default: () => []
    },
    serviceAudioSequences: {
      type: Array,
      default: () => Array(10).fill().map(() => [])
    },
    separateDirections: {
      type: Object,
      default: () => ({
        arrival: false,
        departure: false,
        welcome: false,
        terminal: false
      })
    }
  },
  data() {
    return {
      // 使用props作为初始值，在组件内部管理本地状态
      defaultArrivalAudioSequence: [...this.defaultArrivalAudioSequence],
      defaultDepartureAudioSequence: [...this.defaultDepartureAudioSequence],
      welcomeAudioSequence: [...this.welcomeAudioSequence], // 欢迎语音频序列
      terminalAudioSequence: [...this.terminalAudioSequence], // 终点站语音频序列
      // 上下行分别的音频序列
      upArrivalAudioSequence: [...this.upArrivalAudioSequence],
      downArrivalAudioSequence: [...this.downArrivalAudioSequence],
      upDepartureAudioSequence: [...this.upDepartureAudioSequence],
      downDepartureAudioSequence: [...this.downDepartureAudioSequence],
      upWelcomeAudioSequence: [...this.upWelcomeAudioSequence],
      downWelcomeAudioSequence: [...this.downWelcomeAudioSequence],
      upTerminalAudioSequence: [...this.upTerminalAudioSequence],
      downTerminalAudioSequence: [...this.downTerminalAudioSequence],
      // 上下行不同设置
      separateDirections: {...this.separateDirections},
      serviceAudioSequences: this.serviceAudioSequences.map(seq => [...seq]), // 10个服务语音频序列
      activeTab: 'arrival',
      selectedAudio: null,
      selectedSequenceAudio: null,
      isPlaying: false,
      currentAudioIndex: -1,
      playProgress: 0,
      audioElements: [],
      enableMultiLanguage: false,
      languageCount: 2,
      languages: [
        { name: '中文', code: 'zh' },
        { name: '英文', code: 'en' },
        { name: '日文', code: 'ja' },
        { name: '韩文', code: 'ko' },
        { name: '俄文', code: 'ru' }
      ],
      stationTabs: [
        { label: '默认进站语', value: 'arrival' },
        { label: '默认出站语', value: 'departure' },
        { label: '欢迎语', value: 'welcome' },
        { label: '终点站语', value: 'terminal' },
        ...Array.from({ length: 10 }, (_, i) => ({ label: `服务语${i + 1}`, value: `service${i + 1}` }))
      ],
      audioConfigTimeout: null,
      selectedTargetSequence: 'unified'
    };
  },
  computed: {
    mainTabs() {
      return this.stationTabs.slice(0, 4);
    },
    serviceTabs() {
      return this.stationTabs.slice(4);
    },
    currentStations() {
      return this.currentDirection === 'up' ? this.upDirectionStations : this.downDirectionStations;
    },
    currentSequence() {
      // 如果当前tab支持上下行分别设置且已启用
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        // 返回当前方向的序列
        const direction = this.currentDirection;
        if (this.activeTab === 'arrival') {
          return direction === 'up' ? this.upArrivalAudioSequence : this.downArrivalAudioSequence;
        } else if (this.activeTab === 'departure') {
          return direction === 'up' ? this.upDepartureAudioSequence : this.downDepartureAudioSequence;
        } else if (this.activeTab === 'welcome') {
          return direction === 'up' ? this.upWelcomeAudioSequence : this.downWelcomeAudioSequence;
        } else if (this.activeTab === 'terminal') {
          return direction === 'up' ? this.upTerminalAudioSequence : this.downTerminalAudioSequence;
        }
      }
      
      // 使用统一序列
      if (this.activeTab === 'arrival') {
        return this.defaultArrivalAudioSequence;
      } else if (this.activeTab === 'departure') {
        return this.defaultDepartureAudioSequence;
      } else if (this.activeTab === 'welcome') {
        return this.welcomeAudioSequence;
      } else if (this.activeTab === 'terminal') {
        return this.terminalAudioSequence;
      } else if (this.activeTab.startsWith('service')) {
        const serviceIndex = parseInt(this.activeTab.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          return this.serviceAudioSequences[serviceIndex];
        }
      }
      return [];
    },
    canMoveUp() {
      // 上下行不同
      if (["arrival", "departure", "welcome", "terminal"].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        if (typeof this.selectedSequenceAudio === 'string') {
          const parts = this.selectedSequenceAudio.split('-');
          if (parts.length === 2) {
            const index = parseInt(parts[1]);
            return index > 0;
          }
        }
        return false;
      }
      // 统一序列
      return this.selectedSequenceAudio !== null && this.selectedSequenceAudio > 0;
    },
    canMoveDown() {
      // 上下行不同
      if (["arrival", "departure", "welcome", "terminal"].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        if (typeof this.selectedSequenceAudio === 'string') {
          const parts = this.selectedSequenceAudio.split('-');
          if (parts.length === 2) {
            const direction = parts[0];
            const index = parseInt(parts[1]);
            const sequence = this.getDirectionSequence(direction);
            return index < sequence.length - 1;
          }
        }
        return false;
      }
      // 统一序列
      return this.selectedSequenceAudio !== null && 
             this.selectedSequenceAudio < this.currentSequence.length - 1;
    },
    visibleLanguages() {
      if (!this.languageSettings || !this.languageSettings.languages) return [];
      // 确保语言数量为数字类型，避免 undefined 错误
      const count = parseInt(this.languageSettings.count) || 1;
      return this.languageSettings.languages.slice(0, count);
    },
    selectedStation() {
      if (this.selectedStationIndex >= 0 && this.selectedStationIndex < this.currentStations.length) {
        return this.currentStations[this.selectedStationIndex];
      }
      return null;
    },
    isFirstStation() {
      return this.selectedStationIndex === 0;
    },
    activeLanguageIndices() {
      // 确保语言数量为数字类型，避免 undefined 错误
      const count = parseInt(this.languageSettings.count) || 1;
      return Array.from({ length: count - 1 }, (_, i) => i + 1);
    },
    isPlayingArrival() {
      return this.activeTab === 'arrival' ? this.isPlaying : false;
    },
    isPlayingDeparture() {
      return this.activeTab === 'departure' ? this.isPlaying : false;
    },
    playProgressArrival() {
      return this.activeTab === 'arrival' ? this.playProgress : 0;
    },
    playProgressDeparture() {
      return this.activeTab === 'departure' ? this.playProgress : 0;
    }
  },
  watch: {
    activeTab() {
      this.selectedSequenceAudio = null;
      this.stopPlayback();
      // 根据当前tab配置，初始化目标序列
      if (["arrival", "departure", "welcome", "terminal"].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        this.selectedTargetSequence = 'up';
      } else {
        this.selectedTargetSequence = 'unified';
      }
    },
    
    // 监听props变化并同步到本地数据
    'defaultArrivalAudioSequence': {
      handler(newVal) {
        if (JSON.stringify(this.defaultArrivalAudioSequence) !== JSON.stringify(newVal)) {
          this.defaultArrivalAudioSequence = [...newVal];
        }
      },
      deep: true
    },
    'defaultDepartureAudioSequence': {
      handler(newVal) {
        if (JSON.stringify(this.defaultDepartureAudioSequence) !== JSON.stringify(newVal)) {
          this.defaultDepartureAudioSequence = [...newVal];
        }
      },
      deep: true
    },
    'welcomeAudioSequence': {
      handler(newVal) {
        if (JSON.stringify(this.welcomeAudioSequence) !== JSON.stringify(newVal)) {
          this.welcomeAudioSequence = [...newVal];
        }
      },
      deep: true
    },
    'terminalAudioSequence': {
      handler(newVal) {
        if (JSON.stringify(this.terminalAudioSequence) !== JSON.stringify(newVal)) {
          this.terminalAudioSequence = [...newVal];
        }
      },
      deep: true
    },
    'separateDirections': {
      handler(newVal) {
        if (JSON.stringify(this.separateDirections) !== JSON.stringify(newVal)) {
          this.separateDirections = {...newVal};
        }
      },
      deep: true
    },
    'serviceAudioSequences': {
      handler(newVal) {
        if (JSON.stringify(this.serviceAudioSequences) !== JSON.stringify(newVal)) {
          this.serviceAudioSequences = newVal.map(seq => [...seq]);
        }
      },
      deep: true
    }
  },
  methods: {
    // 站点选择相关方法
    switchDirection(direction) {
      if (this.currentDirection !== direction) {
        this.$emit('directionChanged', direction);
      }
    },
    
    selectStation(index) {
      this.$emit('selectStation', index);
    },
    
    // 更新语言设置
    updateLanguageSettings() {
      // 发送事件通知父组件更新语言设置
      this.$emit('updateLanguageSettings', this.languageSettings);
    },
    
    // 添加预设音频到序列
    addPresetAudio(type, preset) {
      const stations = this.currentStations;
      if (!stations || stations.length === 0) {
        alert('请先上传站点数据');
        return;
      }

      // 如果是起点站进站，显示警告并返回
      if (type === 'arrival' && preset.startsWith('first') && this.isFirstStation) {
        alert('起点站不需要进站语音');
        return;
      }

      let placeholder = '';
      
      // 根据预设类型设置对应的占位符
      if (preset.startsWith('current')) {
        const langIndex = preset.length > 7 ? parseInt(preset.substring(7)) : 0;
        if (langIndex > 0) {
          placeholder = `【当前站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【当前站】';
        }
      }
      else if (preset.startsWith('terminal')) {
        const langIndex = preset.length > 8 ? parseInt(preset.substring(8)) : 0;
        if (langIndex > 0) {
          placeholder = `【终点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【终点站】';
        }
      }
      else if (preset.startsWith('first')) {
        const langIndex = preset.length > 5 ? parseInt(preset.substring(5)) : 0;
        if (langIndex > 0) {
          placeholder = `【起点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【起点站】';
        }
      }
      else if (preset === 'alias') {
        placeholder = '【副站名】';
      }

      // 添加到对应的序列中
      if (type === 'arrival') {
        this.defaultArrivalAudioSequence.push(placeholder);
        this.$emit('update:defaultArrivalAudioSequence', this.defaultArrivalAudioSequence);
      } else if (type === 'departure') {
        this.defaultDepartureAudioSequence.push(placeholder);
        this.$emit('update:defaultDepartureAudioSequence', this.defaultDepartureAudioSequence);
      } else if (type === 'welcome') {
        this.welcomeAudioSequence.push(placeholder);
      } else if (type === 'terminal') {
        this.terminalAudioSequence.push(placeholder);
      } else if (type.startsWith('service')) {
        // 获取服务语索引 (0-9)
        const serviceIndex = parseInt(type.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          this.serviceAudioSequences[serviceIndex].push(placeholder);
        }
      }
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },
    
    // 获取显示标签：目录/文件名 或 占位符
    audioLabel(item) {
      if (typeof item === 'string' && item.startsWith('【') && item.endsWith('】')) {
        return item;
      }
      if (typeof item === 'string' && item.includes('/')) {
        return item;
      }
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audio = folder.audios.find(a => a.path === item || a.url === item);
          if (audio) return `${folder.name}/${audio.name}`;
        }
      }
      return '未知音频';
    },

    audioFullPath(item) {
      if (typeof item === 'string' && item.includes('/')) return item;
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audio = folder.audios.find(a => a.path === item || a.url === item);
          if (audio) return audio.path;
        }
      }
      return '';
    },
    
    // 获取实际的音频URL（用于预览）
    getAudioUrl(item, type) {
      // 如果是占位符，查找对应的URL
      if (typeof item === 'string' && item.startsWith('【') && item.endsWith('】')) {
        // 确保有站点数据
        if (!this.currentStations || this.currentStations.length === 0) {
          return null;
        }
        
        // 根据 tab 类型确定“当前站”用于预览（欢迎语固定首站，终点站语固定末站，其它按选中站）
        let contextStation = null;
        if (this.activeTab === 'welcome') {
          contextStation = this.currentStations[0];
        } else if (this.activeTab === 'terminal') {
          contextStation = this.currentStations[this.currentStations.length - 1];
        } else {
          contextStation = this.selectedStation;
        }
        if (!contextStation) return null;
        
        let matchingAudio = null;
        
        // 根据占位符类型查找对应的音频URL
        if (item.includes('当前站')) {
          // 检查是否是文件夹-站点格式 如"文件夹1-当前站"
          const folderMatch = item.match(/【(.+?)-当前站】/);
          if (folderMatch) {
            const folderName = folderMatch[1];
            const stationName = contextStation.name;
            
            // 在指定文件夹中查找站点音频
            const targetFolder = this.audioFolders.find(f => f.name === folderName);
            if (targetFolder && targetFolder.audios) {
              // 查找匹配的音频文件：站名.mp3 或 站名.wav
              matchingAudio = targetFolder.audios.find(a => {
                const nameWithoutExt = a.name.replace(/\.(mp3|wav|ogg|m4a)$/i, '');
                return nameWithoutExt === stationName;
              });
              
              // 如果找不到精确匹配，尝试前缀匹配
              if (!matchingAudio) {
                matchingAudio = targetFolder.audios.find(a => a.name.startsWith(stationName));
              }
            }
          } else {
            // 检查是否包含语言信息
            const langMatch = item.match(/当前站\((.+?)\)/);
            const langIndex = langMatch ? this.languageSettings.languages.findIndex(lang => lang.name === langMatch[1]) : -1;
            
            if (langIndex > 0 && contextStation.multiLanguageSequence && contextStation.multiLanguageSequence.length > (langIndex - 1)) {
              // 从多语言序列获取对应位置的音频路径
              const audioPath = contextStation.multiLanguageSequence[langIndex - 1];
              
              // 获取音频URL
              const fileName = audioPath.split(/[/\\]/).pop();
              for (const folder of this.audioFolders) {
                if (folder.audios) {
                  const audio = folder.audios.find(a => a.path === audioPath) || 
                                folder.audios.find(a => a.name === fileName);
                  if (audio) return audio.url;
                }
              }
            } else {
              // 使用默认站点名称匹配 - 在第一个文件夹中查找
              const stationName = contextStation.name;
              for (const folder of this.audioFolders) {
                if (folder.audios) {
                  matchingAudio = folder.audios.find(a => a.name.startsWith(stationName));
                  if (matchingAudio) break;
                }
              }
            }
          }
        }
        else if (item.includes('副站名')) {
          // 处理副站名占位符
          if (selectedStation.useAlias && selectedStation.aliasSequence && selectedStation.aliasSequence.length > 0) {
            // 使用副站名序列的第一个音频
            const audioPath = selectedStation.aliasSequence[0];
            
            // 获取音频URL
            const allAudios = [...this.stationAudios, ...this.hintAudios];
            const fileName = audioPath.split(/[/\\]/).pop();
            const audio = allAudios.find(a => a.path === audioPath) || 
                          allAudios.find(a => a.name === fileName);
            
            if (audio) return audio.url;
          }
        }
        else if (item.includes('终点站')) {
          // 检查是否是文件夹-站点格式 如"文件夹1-终点站"
          const folderMatch = item.match(/【(.+?)-终点站】/);
          if (folderMatch) {
            const folderName = folderMatch[1];
            
            // 获取终点站名称
            if (this.currentStations.length > 0) {
              const terminalStation = this.currentStations[this.currentStations.length - 1];
              const stationName = terminalStation.name;
              
              // 在指定文件夹中查找终点站音频
              const targetFolder = this.audioFolders.find(f => f.name === folderName);
              if (targetFolder && targetFolder.audios) {
                // 查找匹配的音频文件：站名.mp3 或 站名.wav
                matchingAudio = targetFolder.audios.find(a => {
                  const nameWithoutExt = a.name.replace(/\.(mp3|wav|ogg|m4a)$/i, '');
                  return nameWithoutExt === stationName;
                });
                
                // 如果找不到精确匹配，尝试前缀匹配
                if (!matchingAudio) {
                  matchingAudio = targetFolder.audios.find(a => a.name.startsWith(stationName));
                }
              }
            }
          } else {
            const langMatch = item.match(/终点站\((.+?)\)/);
            const langIndex = langMatch ? this.languageSettings.languages.findIndex(lang => lang.name === langMatch[1]) : -1;
            
            if (langIndex > 0 && selectedStation.multiLanguageSequence && selectedStation.multiLanguageSequence.length > (langIndex - 1)) {
              // 从多语言序列获取对应位置的音频路径
              const audioPath = selectedStation.multiLanguageSequence[langIndex - 1];
              
              // 获取音频URL
              const fileName = audioPath.split(/[/\\]/).pop();
              for (const folder of this.audioFolders) {
                if (folder.audios) {
                  const audio = folder.audios.find(a => a.path === audioPath) || 
                                folder.audios.find(a => a.name === fileName);
                  if (audio) return audio.url;
                }
              }
            } else {
              // 使用默认终点站逻辑
              if (this.currentStations.length > 0) {
                const terminalStation = this.currentStations[this.currentStations.length - 1];
                const stationName = terminalStation.name;
                for (const folder of this.audioFolders) {
                  if (folder.audios) {
                    matchingAudio = folder.audios.find(a => a.name.startsWith(stationName));
                    if (matchingAudio) break;
                  }
                }
              }
            }
          }
        }
        else if (item.includes('起点站')) {
          // 检查是否是文件夹-站点格式 如"文件夹1-起点站"
          const folderMatch = item.match(/【(.+?)-起点站】/);
          if (folderMatch) {
            const folderName = folderMatch[1];
            
            // 获取起点站名称
            if (this.currentStations.length > 0) {
              const firstStation = this.currentStations[0];
              const stationName = firstStation.name;
              
              // 在指定文件夹中查找起点站音频
              const targetFolder = this.audioFolders.find(f => f.name === folderName);
              if (targetFolder && targetFolder.audios) {
                // 查找匹配的音频文件：站名.mp3 或 站名.wav
                matchingAudio = targetFolder.audios.find(a => {
                  const nameWithoutExt = a.name.replace(/\.(mp3|wav|ogg|m4a)$/i, '');
                  return nameWithoutExt === stationName;
                });
                
                // 如果找不到精确匹配，尝试前缀匹配
                if (!matchingAudio) {
                  matchingAudio = targetFolder.audios.find(a => a.name.startsWith(stationName));
                }
              }
            }
          } else {
            const langMatch = item.match(/起点站\((.+?)\)/);
            const langIndex = langMatch ? this.languageSettings.languages.findIndex(lang => lang.name === langMatch[1]) : -1;
            
            if (langIndex > 0 && selectedStation.multiLanguageSequence && selectedStation.multiLanguageSequence.length > (langIndex - 1)) {
              // 从多语言序列获取对应位置的音频路径
              const audioPath = selectedStation.multiLanguageSequence[langIndex - 1];
              
              // 获取音频URL
              const fileName = audioPath.split(/[/\\]/).pop();
              for (const folder of this.audioFolders) {
                if (folder.audios) {
                  const audio = folder.audios.find(a => a.path === audioPath) || 
                                folder.audios.find(a => a.name === fileName);
                  if (audio) return audio.url;
                }
              }
            } else {
              // 使用默认起点站逻辑
              if (this.currentStations.length > 0) {
                const firstStation = this.currentStations[0];
                const stationName = firstStation.name;
                for (const folder of this.audioFolders) {
                  if (folder.audios) {
                    matchingAudio = folder.audios.find(a => a.name.startsWith(stationName));
                    if (matchingAudio) break;
                  }
                }
              }
            }
          }
        }
        
        // 如果找到匹配的音频，返回其URL
        if (matchingAudio) {
          return matchingAudio.url;
        }
        
        return null;
      }
      
      // 如果是字符串路径或url，严格按 path/url 匹配
      if (typeof item === 'string') {
        for (const folder of this.audioFolders) {
          if (folder.audios) {
            const audio = folder.audios.find(a => a.path === item || a.url === item);
            if (audio) return audio.url;
          }
        }
        return null;
      }
      
      // 如果是blob URL，直接返回
      return item;
    },
    
    // 将blob URL转换为文件路径
    convertBlobToPath(blobUrl) {
      if (typeof blobUrl !== 'string' || !blobUrl.startsWith('blob:')) {
        return blobUrl; // 如果不是blob URL，直接返回
      }
      
      // 从所有音频文件夹中查找
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audio = folder.audios.find(a => a.url === blobUrl);
          if (audio) return audio.path;
        }
      }
      
      return blobUrl;
    },
    
    // 添加音频到当前序列（当上下行不同开启时，根据选中的目标序列添加）
    addToSequence() {
      if (!this.selectedAudio) return;
      
      if (this.currentSequence.length >= 10) {
        alert('最多只能添加10个音频文件到序列中');
        return;
      }
      
      // 使用完整的音频路径，包含文件夹前缀
      const audioPath = this.selectedAudio.path;
      
      // 检查是否为上下行分别设置
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        // 根据用户选中的目标序列决定方向，未明确则默认上行
        let direction = this.selectedTargetSequence;
        if (direction === 'unified') direction = 'up';
        if (this.activeTab === 'arrival') {
          if (direction === 'up') {
            this.upArrivalAudioSequence.push(audioPath);
          } else {
            this.downArrivalAudioSequence.push(audioPath);
          }
        } else if (this.activeTab === 'departure') {
          if (direction === 'up') {
            this.upDepartureAudioSequence.push(audioPath);
          } else {
            this.downDepartureAudioSequence.push(audioPath);
          }
        } else if (this.activeTab === 'welcome') {
          if (direction === 'up') {
            this.upWelcomeAudioSequence.push(audioPath);
          } else {
            this.downWelcomeAudioSequence.push(audioPath);
          }
        } else if (this.activeTab === 'terminal') {
          if (direction === 'up') {
            this.upTerminalAudioSequence.push(audioPath);
          } else {
            this.downTerminalAudioSequence.push(audioPath);
          }
        }
        return;
      }
      
      // 添加到统一序列
      if (this.activeTab === 'arrival') {
        this.defaultArrivalAudioSequence.push(audioPath);
        this.$emit('update:defaultArrivalAudioSequence', this.defaultArrivalAudioSequence);
      } else if (this.activeTab === 'departure') {
        this.defaultDepartureAudioSequence.push(audioPath);
        this.$emit('update:defaultDepartureAudioSequence', this.defaultDepartureAudioSequence);
      } else if (this.activeTab === 'welcome') {
        this.welcomeAudioSequence.push(audioPath);
      } else if (this.activeTab === 'terminal') {
        this.terminalAudioSequence.push(audioPath);
      } else if (this.activeTab.startsWith('service')) {
        // 获取服务语索引 (0-9)
        const serviceIndex = parseInt(this.activeTab.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          this.serviceAudioSequences[serviceIndex].push(audioPath);
          // 不需要发送emit事件，因为serviceAudioSequences是本地数据
        }
      }
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },
    
    // 新增：添加音频到指定方向的序列
    addToDirectionSequence(direction) {
      if (!this.selectedAudio) return;
      if ([
        (direction === 'up' ? this.getDirectionSequence('up') : this.getDirectionSequence('down')).length
      ] >= 10) {
        alert('最多只能添加10个音频文件到序列中');
        return;
      }
      const audioPath = this.selectedAudio.path;
      if (this.activeTab === 'arrival') {
        if (direction === 'up') {
          this.upArrivalAudioSequence.push(audioPath);
        } else {
          this.downArrivalAudioSequence.push(audioPath);
        }
      } else if (this.activeTab === 'departure') {
        if (direction === 'up') {
          this.upDepartureAudioSequence.push(audioPath);
        } else {
          this.downDepartureAudioSequence.push(audioPath);
        }
      } else if (this.activeTab === 'welcome') {
        if (direction === 'up') {
          this.upWelcomeAudioSequence.push(audioPath);
        } else {
          this.downWelcomeAudioSequence.push(audioPath);
        }
      } else if (this.activeTab === 'terminal') {
        if (direction === 'up') {
          this.upTerminalAudioSequence.push(audioPath);
        } else {
          this.downTerminalAudioSequence.push(audioPath);
        }
      }
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },
    
    // 从当前序列中移除音频
    removeFromSequence() {
      if (this.selectedSequenceAudio === null) return;
      
      // 处理上下行分别设置的情况
      if (["arrival", "departure", "welcome", "terminal"].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        // 解析选择的索引格式（如"up-2"或"down-1"）
        const parts = String(this.selectedSequenceAudio).split('-');
        if (parts.length === 2) {
          const direction = parts[0];
          const index = parseInt(parts[1]);
          let sequence = this.getDirectionSequence(direction);
          if (index >= 0 && index < sequence.length) {
            sequence.splice(index, 1);
          }
        }
        this.selectedSequenceAudio = null;
        // 自动保存音频配置
        this.emitAudioConfigChange();
        return;
      }
      
      // 处理统一序列
      if (this.activeTab === 'arrival') {
        this.defaultArrivalAudioSequence.splice(this.selectedSequenceAudio, 1);
        this.$emit('update:defaultArrivalAudioSequence', this.defaultArrivalAudioSequence);
      } else if (this.activeTab === 'departure') {
        this.defaultDepartureAudioSequence.splice(this.selectedSequenceAudio, 1);
        this.$emit('update:defaultDepartureAudioSequence', this.defaultDepartureAudioSequence);
      } else if (this.activeTab === 'welcome') {
        this.welcomeAudioSequence.splice(this.selectedSequenceAudio, 1);
      } else if (this.activeTab === 'terminal') {
        this.terminalAudioSequence.splice(this.selectedSequenceAudio, 1);
      } else if (this.activeTab.startsWith('service')) {
        // 获取服务语索引 (0-9)
        const serviceIndex = parseInt(this.activeTab.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          this.serviceAudioSequences[serviceIndex].splice(this.selectedSequenceAudio, 1);
        }
      }
      
      // 重置选择
      this.selectedSequenceAudio = null;
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },
    
    // 向上移动音频
    moveUp() {
      if (!this.canMoveUp) return;
      // 上下行不同时
      if (["arrival", "departure", "welcome", "terminal"].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        const parts = String(this.selectedSequenceAudio).split('-');
        if (parts.length === 2) {
          const direction = parts[0];
          const index = parseInt(parts[1]);
          let sequence = this.getDirectionSequence(direction);
          if (index > 0) {
            const temp = sequence[index];
            sequence[index] = sequence[index - 1];
            sequence[index - 1] = temp;
            this.selectedSequenceAudio = `${direction}-${index - 1}`;
          }
        }
        return;
      }
      // 统一序列
      const index = this.selectedSequenceAudio;
      let sequence = this.currentSequence;
      if (index > 0) {
        const temp = sequence[index];
        sequence[index] = sequence[index - 1];
        sequence[index - 1] = temp;
        this.selectedSequenceAudio = index - 1;
      }
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },

    // 向下移动音频
    moveDown() {
      if (!this.canMoveDown) return;
      // 上下行不同时
      if (["arrival", "departure", "welcome", "terminal"].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        const parts = String(this.selectedSequenceAudio).split('-');
        if (parts.length === 2) {
          const direction = parts[0];
          const index = parseInt(parts[1]);
          let sequence = this.getDirectionSequence(direction);
          if (index < sequence.length - 1) {
            const temp = sequence[index];
            sequence[index] = sequence[index + 1];
            sequence[index + 1] = temp;
            this.selectedSequenceAudio = `${direction}-${index + 1}`;
          }
        }
        return;
      }
      // 统一序列
      const index = this.selectedSequenceAudio;
      let sequence = this.currentSequence;
      if (index < sequence.length - 1) {
        const temp = sequence[index];
        sequence[index] = sequence[index + 1];
        sequence[index + 1] = temp;
        this.selectedSequenceAudio = index + 1;
      }
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },
    
    // 预览当前音频序列
    previewAudioSequence() {
      if (this.isPlaying) {
        this.stopPlayback();
        return;
      }
      
      if (this.currentSequence.length === 0) {
        alert('请先添加音频到播放序列');
        return;
      }
      
      // 开始播放
      this.isPlaying = true;
      this.currentAudioIndex = 0;
      this.playProgress = 0;
      
      // 清除之前的音频元素
      this.audioElements = [];
      
      // 创建所有音频元素，处理占位符
      const validAudios = [];
      
      for (const item of this.currentSequence) {
        const url = this.getAudioUrl(item, this.activeTab);
        if (url) {
          const audio = new Audio(url);
          this.audioElements.push(audio);
          validAudios.push(item);
        }
      }
      
      if (this.audioElements.length === 0) {
        alert('无法找到有效的音频文件进行预览');
        this.isPlaying = false;
        return;
      }
      
      // 为每个音频添加结束事件，播放下一个
      this.audioElements.forEach((audio, index) => {
        audio.addEventListener('ended', () => {
          if (index < this.audioElements.length - 1) {
            this.currentAudioIndex = index + 1;
            this.playProgress = ((index + 1) / this.audioElements.length) * 100;
            this.audioElements[index + 1].play();
          } else {
            // 最后一个音频播放完毕
            this.isPlaying = false;
            this.currentAudioIndex = -1;
            this.playProgress = 100;
            
            // 延迟重置进度条
            setTimeout(() => {
              this.playProgress = 0;
            }, 1000);
          }
        });
      });
      
      // 开始播放第一个音频
      this.audioElements[0].play();
    },
    
    // 停止播放
    stopPlayback() {
      if (!this.isPlaying) return;
      
      // 停止所有音频
      this.audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      
      // 重置状态
      this.isPlaying = false;
      this.currentAudioIndex = -1;
      this.playProgress = 0;
      this.audioElements = [];
    },
    
    // 生成JSON
    generateJson() {
      // 将所有blob URL转换为文件路径
      const arrivalAudioSequence = this.defaultArrivalAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const departureAudioSequence = this.defaultDepartureAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const welcomeAudioSequence = this.welcomeAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const terminalAudioSequence = this.terminalAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      // 处理服务语序列
      const serviceAudioSequences = this.serviceAudioSequences.map(sequence => {
        return sequence.map(item => {
          if (typeof item === 'string' && item.startsWith('blob:')) {
            return this.convertBlobToPath(item);
          }
          return item;
        });
      });
      
      // 处理上下行分别的音频序列
      const upArrivalAudioSequence = this.upArrivalAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const downArrivalAudioSequence = this.downArrivalAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const upDepartureAudioSequence = this.upDepartureAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const downDepartureAudioSequence = this.downDepartureAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const upWelcomeAudioSequence = this.upWelcomeAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const downWelcomeAudioSequence = this.downWelcomeAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const upTerminalAudioSequence = this.upTerminalAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });
      
      const downTerminalAudioSequence = this.downTerminalAudioSequence.map(item => {
        if (typeof item === 'string' && item.startsWith('blob:')) {
          return this.convertBlobToPath(item);
        }
        return item;
      });

      // 发射事件，将音频序列和多语言设置传递给父组件
      this.$emit('generateJson', {
        arrivalAudioSequence: arrivalAudioSequence,
        departureAudioSequence: departureAudioSequence,
        welcomeAudioSequence: welcomeAudioSequence,
        terminalAudioSequence: terminalAudioSequence,
        // 上下行分别的配置
        separateDirections: this.separateDirections,
        upArrivalAudioSequence: upArrivalAudioSequence,
        downArrivalAudioSequence: downArrivalAudioSequence,
        upDepartureAudioSequence: upDepartureAudioSequence,
        downDepartureAudioSequence: downDepartureAudioSequence,
        upWelcomeAudioSequence: upWelcomeAudioSequence,
        downWelcomeAudioSequence: downWelcomeAudioSequence,
        upTerminalAudioSequence: upTerminalAudioSequence,
        downTerminalAudioSequence: downTerminalAudioSequence,
        serviceAudioSequences: serviceAudioSequences,
        languageSettings: this.languageSettings
      });
    },
    
    getActiveTabName() {
      if (this.activeTab === 'arrival') {
        return '默认进站语音频配置';
      } else if (this.activeTab === 'departure') {
        return '默认出站语音频配置';
      } else if (this.activeTab === 'welcome') {
        return '欢迎语音频配置（第二个站的预报）';
      } else if (this.activeTab === 'terminal') {
        return '终点站语音频配置（最后一个站的进站）';
      } else if (this.activeTab.startsWith('service')) {
        return '服务语' + this.activeTab.slice(7) + '配置';
      }
      return '';
    },
    
    // 站点显示名称（兼容多语言）
    getStationDisplayName(station) {
      if (station.name) return station.name;
      if (this.languageSettings && this.languageSettings.languages) {
        for (let i = 0; i < this.languageSettings.languages.length; i++) {
          const code = this.languageSettings.languages[i].code;
          if (code && station['name_' + code]) {
            return station['name_' + code];
          }
        }
      }
      return '未命名';
    },

    // 获取指定方向的音频序列
    getDirectionSequence(direction) {
      if (this.activeTab === 'arrival') {
        return direction === 'up' ? this.upArrivalAudioSequence : this.downArrivalAudioSequence;
      } else if (this.activeTab === 'departure') {
        return direction === 'up' ? this.upDepartureAudioSequence : this.downDepartureAudioSequence;
      } else if (this.activeTab === 'welcome') {
        return direction === 'up' ? this.upWelcomeAudioSequence : this.downWelcomeAudioSequence;
      } else if (this.activeTab === 'terminal') {
        return direction === 'up' ? this.upTerminalAudioSequence : this.downTerminalAudioSequence;
      }
      return [];
    },

    // 处理上下行不同选项变化
    onSeparateDirectionChange() {
      // 当切换为上下行分别设置时，将当前的统一序列复制到两个方向
      if (this.separateDirections[this.activeTab]) {
        const currentSeq = [...this.currentSequence];
        if (this.activeTab === 'arrival') {
          this.upArrivalAudioSequence = [...currentSeq];
          this.downArrivalAudioSequence = [...currentSeq];
        } else if (this.activeTab === 'departure') {
          this.upDepartureAudioSequence = [...currentSeq];
          this.downDepartureAudioSequence = [...currentSeq];
        } else if (this.activeTab === 'welcome') {
          this.upWelcomeAudioSequence = [...currentSeq];
          this.downWelcomeAudioSequence = [...currentSeq];
        } else if (this.activeTab === 'terminal') {
          this.upTerminalAudioSequence = [...currentSeq];
          this.downTerminalAudioSequence = [...currentSeq];
        }
      }
      this.selectedSequenceAudio = null;
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },

    // 处理序列选择变化，同时记录目标序列
    onSequenceSelectionChange(direction) {
      this.selectedTargetSequence = direction;
    },
    
    // 新增：插入占位符到指定方向
    addPresetAudioToDirection(type, preset, direction) {
      let placeholder = '';
      if (preset.startsWith('current')) {
        placeholder = '【当前站】';
      } else if (preset.startsWith('terminal')) {
        placeholder = '【终点站】';
      } else if (preset.startsWith('first')) {
        placeholder = '【起点站】';
      } else if (preset === 'alias') {
        placeholder = '【副站名】';
      }
      if (type === 'arrival') {
        if (direction === 'up') {
          this.upArrivalAudioSequence.push(placeholder);
        } else {
          this.downArrivalAudioSequence.push(placeholder);
        }
      } else if (type === 'departure') {
        if (direction === 'up') {
          this.upDepartureAudioSequence.push(placeholder);
        } else {
          this.downDepartureAudioSequence.push(placeholder);
        }
      } else if (type === 'welcome') {
        if (direction === 'up') {
          this.upWelcomeAudioSequence.push(placeholder);
        } else {
          this.downWelcomeAudioSequence.push(placeholder);
        }
      } else if (type === 'terminal') {
        if (direction === 'up') {
          this.upTerminalAudioSequence.push(placeholder);
        } else {
          this.downTerminalAudioSequence.push(placeholder);
        }
      }
    },

    // 添加基于文件夹的占位符
    addFolderPlaceholder(type, folderName, stationType) {
      const stations = this.currentStations;
      if (!stations || stations.length === 0) {
        alert('请先上传站点数据');
        return;
      }

      // 如果是起点站进站，显示警告并返回
      if (type === 'arrival' && stationType === 'first' && this.isFirstStation) {
        alert('起点站不需要进站语音');
        return;
      }

      let placeholder = `【${folderName}-${stationType === 'current' ? '当前站' : stationType === 'terminal' ? '终点站' : '起点站'}】`;

      // 添加到对应的序列中
      if (type === 'arrival') {
        this.defaultArrivalAudioSequence.push(placeholder);
      } else if (type === 'departure') {
        this.defaultDepartureAudioSequence.push(placeholder);
      } else if (type === 'welcome') {
        this.welcomeAudioSequence.push(placeholder);
      } else if (type === 'terminal') {
        this.terminalAudioSequence.push(placeholder);
      } else if (type.startsWith('service')) {
        // 获取服务语索引 (0-9)
        const serviceIndex = parseInt(type.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          this.serviceAudioSequences[serviceIndex].push(placeholder);
        }
      }
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },

    // 添加基于文件夹的占位符到指定方向
    addFolderPlaceholderToDirection(type, folderName, stationType, direction) {
      const stations = this.currentStations;
      if (!stations || stations.length === 0) {
        alert('请先上传站点数据');
        return;
      }

      // 如果是起点站进站，显示警告并返回
      if (type === 'arrival' && stationType === 'first' && this.isFirstStation) {
        alert('起点站不需要进站语音');
        return;
      }

      let placeholder = `【${folderName}-${stationType === 'current' ? '当前站' : stationType === 'terminal' ? '终点站' : '起点站'}】`;

      if (type === 'arrival') {
        if (direction === 'up') {
          this.upArrivalAudioSequence.push(placeholder);
        } else {
          this.downArrivalAudioSequence.push(placeholder);
        }
      } else if (type === 'departure') {
        if (direction === 'up') {
          this.upDepartureAudioSequence.push(placeholder);
        } else {
          this.downDepartureAudioSequence.push(placeholder);
        }
      } else if (type === 'welcome') {
        if (direction === 'up') {
          this.upWelcomeAudioSequence.push(placeholder);
        } else {
          this.downWelcomeAudioSequence.push(placeholder);
        }
      } else if (type === 'terminal') {
        if (direction === 'up') {
          this.upTerminalAudioSequence.push(placeholder);
        } else {
          this.downTerminalAudioSequence.push(placeholder);
        }
      }
      
      // 自动保存音频配置
      this.emitAudioConfigChange();
    },
    
    // 新增：预览指定方向的音频序列
    previewDirectionSequence(direction) {
      if (this.isPlaying) {
        this.stopPlayback();
        return;
      }
      const sequence = this.getDirectionSequence(direction);
      if (!sequence || sequence.length === 0) {
        alert('请先添加音频到' + (direction === 'up' ? '上行' : '下行') + '播放序列');
        return;
      }
      // 当启用上下行分别设置且预览方向与当前方向不一致时，按站名在目标方向定位
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab] && direction !== this.currentDirection) {
        const sourceStations = this.currentDirection === 'up' ? this.upDirectionStations : this.downDirectionStations;
        const targetStations = direction === 'up' ? this.upDirectionStations : this.downDirectionStations;
        const srcIndex = this.selectedStationIndex;
        if (Array.isArray(sourceStations) && Array.isArray(targetStations) && srcIndex >= 0 && srcIndex < sourceStations.length) {
          const sourceStation = sourceStations[srcIndex];
          const sourceName = sourceStation && sourceStation.name ? sourceStation.name : '';
          if (sourceName) {
            const matchedIndex = targetStations.findIndex(s => s && s.name === sourceName);
            if (matchedIndex === -1) {
              alert((direction === 'up' ? '上行' : '下行') + '方向中不存在同名站点【' + sourceName + '】，无法预览');
              return;
            }
            // 将当前上下文的站点切换为目标方向同名站
            this.$emit('selectStation', matchedIndex);
          }
        }
      }
      this.isPlaying = true;
      this.currentAudioIndex = 0;
      this.playProgress = 0;
      this.audioElements = [];
      for (const item of sequence) {
        const url = this.getAudioUrl(item, this.activeTab);
        if (url) {
          const audio = new Audio(url);
          this.audioElements.push(audio);
        }
      }
      if (this.audioElements.length === 0) {
        alert('无法找到有效的音频文件进行预览');
        this.isPlaying = false;
        return;
      }
      this.audioElements.forEach((audio, index) => {
        audio.addEventListener('ended', () => {
          if (index < this.audioElements.length - 1) {
            this.currentAudioIndex = index + 1;
            this.playProgress = ((index + 1) / this.audioElements.length) * 100;
            this.audioElements[index + 1].play();
          } else {
            this.isPlaying = false;
            this.currentAudioIndex = -1;
            this.playProgress = 100;
            setTimeout(() => {
              this.playProgress = 0;
            }, 1000);
          }
        });
      });
      this.audioElements[0].play();
    },

    
    // 发射音频配置变化事件
    emitAudioConfigChange() {
      // 使用防抖延迟发射，避免频繁触发
      clearTimeout(this.audioConfigTimeout);
      this.audioConfigTimeout = setTimeout(() => {
        this.$emit('audioConfigChanged', {
          defaultArrivalAudioSequence: this.defaultArrivalAudioSequence,
          defaultDepartureAudioSequence: this.defaultDepartureAudioSequence,
          welcomeAudioSequence: this.welcomeAudioSequence,
          terminalAudioSequence: this.terminalAudioSequence,
          upArrivalAudioSequence: this.upArrivalAudioSequence,
          downArrivalAudioSequence: this.downArrivalAudioSequence,
          upDepartureAudioSequence: this.upDepartureAudioSequence,
          downDepartureAudioSequence: this.downDepartureAudioSequence,
          upWelcomeAudioSequence: this.upWelcomeAudioSequence,
          downWelcomeAudioSequence: this.downWelcomeAudioSequence,
          upTerminalAudioSequence: this.upTerminalAudioSequence,
          downTerminalAudioSequence: this.downTerminalAudioSequence,
          serviceAudioSequences: this.serviceAudioSequences,
          separateDirections: this.separateDirections
        });
      }, 500);
    }
  }
};
</script>

<style scoped>
.template-container {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  background-color: white;
  margin-bottom: 15px;
}

.audio-preview {
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

/* 移除空的 .nav-tab-container 规则，避免linter警告 */

.tab-scroll {
  flex-wrap: wrap; /* 允许多行自动换行 */
  /* white-space: nowrap; 取消单行显示 */
}

.nav-tabs .nav-link {
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

/* 去除滚动条相关样式 */
/* .nav-tab-container::-webkit-scrollbar {
  height: 3px;
}

.nav-tab-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.nav-tab-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.nav-tab-container::-webkit-scrollbar-thumb:hover {
  background: #555;
} */

/* 站点选择相关样式 */
.station-list {
  height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 10px;
  background-color: white;
}

.station-item {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  font-size: 0.9rem;
}

.station-item:hover {
  background-color: #f1f8ff;
}

.station-item.active {
  background-color: #e7f3ff;
  border-left: 3px solid #007bff;
}

.badge-station-type {
  margin-left: 5px;
  font-size: 0.7rem;
}

/* 确保左右分栏在小屏幕上也能正常显示 */
@media (max-width: 768px) {
  .col-md-4, .col-md-8 {
    flex: 0 0 100%;
    max-width: 100%;
  }
  
  .station-list {
    height: 200px;
  }
  /* 顶部类型按钮更紧凑 */
  .station-type-btn { max-width: 48%; }
  .template-container { padding: 12px; }
}

.station-type-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.station-type-btn {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 120px;
  margin-bottom: 4px;
}
.station-type-btn.active,
.station-type-btn:active {
  background-color: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}
</style> 